/* Public client-facing RFI form. Accessible via /rfi/{token}. */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { usePageMeta } from "../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL;

const FILE_TYPES = new Set([
    "single_file", "multiple_files", "images",
    "reference_or_file", "text_and_files",
]);
const IMAGE_ONLY = new Set(["images"]);
const SINGLE_ONLY = new Set(["single_file", "reference_or_file"]);

function labelFor(rt) {
    return ({
        single_file: "Upload one file",
        multiple_files: "Upload files",
        images: "Upload images",
        text: "Provide text",
        reference: "Provide a reference or identifier",
        reference_or_file: "Provide a reference OR upload a file",
        text_and_files: "Provide text and/or files",
    })[rt] || rt;
}

function bytes(n) {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function PublicRFIForm() {
    usePageMeta({ title: "Information Request | Studio G4", robots: "noindex,nofollow" });
    const { token } = useParams();
    const [row, setRow] = useState(null);
    const [error, setError] = useState("");
    const [msg, setMsg] = useState("");
    const [drafts, setDrafts] = useState({});
    const [busyFile, setBusyFile] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const fileInputs = useRef({});

    const load = async () => {
        try {
            const r = await fetch(`${API}/api/public/rfi/${token}`);
            if (!r.ok) { setError(await r.text() || "Request not found"); return; }
            setRow(await r.json());
        } catch (e) { setError("Network error"); }
    };
    useEffect(() => { load(); }, [token]);

    const sendResponse = async (itemId, payload) => {
        try {
            const r = await fetch(`${API}/api/public/rfi/${token}/items/${itemId}/respond`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!r.ok) { setError(await r.text() || "Save failed"); return; }
            setMsg("Saved");
            load();
        } catch (e) { setError("Network error"); }
    };

    const uploadFile = async (itemId, file) => {
        if (!file) return;
        setBusyFile(b => ({ ...b, [itemId]: true }));
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await fetch(
                `${API}/api/public/rfi/${token}/items/${itemId}/file`,
                { method: "POST", body: fd });
            if (!r.ok) { setError(await r.text() || "Upload failed"); return; }
            setMsg(`Uploaded ${file.name}`);
            await load();
        } catch (e) { setError("Network error"); }
        finally { setBusyFile(b => ({ ...b, [itemId]: false })); }
    };

    const removeFile = async (itemId, fileId) => {
        setBusyFile(b => ({ ...b, [itemId]: true }));
        setError("");
        try {
            const r = await fetch(
                `${API}/api/public/rfi/${token}/items/${itemId}/file/${fileId}`,
                { method: "DELETE" });
            if (!r.ok) { setError(await r.text() || "Remove failed"); return; }
            setMsg("Removed");
            await load();
        } catch (e) { setError("Network error"); }
        finally { setBusyFile(b => ({ ...b, [itemId]: false })); }
    };

    const onText = (itemId) => (e) => setDrafts(d => ({ ...d, [itemId]: { ...(d[itemId] || {}), text: e.target.value } }));
    const onRef = (itemId) => (e) => setDrafts(d => ({ ...d, [itemId]: { ...(d[itemId] || {}), reference: e.target.value } }));
    const commit = (itemId) => () => sendResponse(itemId, { action: "set_text_or_reference", ...drafts[itemId] });
    const notAvailable = (itemId) => () => sendResponse(itemId, { action: "not_available" });

    const onSubmit = async () => {
        setSubmitting(true);
        try {
            const r = await fetch(`${API}/api/public/rfi/${token}/submit`, { method: "POST" });
            if (!r.ok) { setError(await r.text() || "Submit failed"); return; }
            setMsg("Submitted. Thank you.");
            load();
        } catch (e) { setError("Network error"); }
        finally { setSubmitting(false); }
    };

    if (error && !row) return <div className="max-w-xl mx-auto p-8 text-terracotta" data-testid="rfi-public-error">{error}</div>;
    if (!row) return <div className="max-w-xl mx-auto p-8 text-[#666]">Loading…</div>;

    const submittable = row.status === "Issued" || row.status === "Partially Received"
                        || row.status === "Clarification Requested";
    return (<div className="min-h-screen bg-[#FAF8F3]">
        <header className="bg-white border-b border-[#E5E2DC] px-6 py-4">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
                <div className="font-serif text-lg text-charcoal">Studio G4</div>
                <div className="text-[12px] text-[#666]">Information Request</div>
            </div>
        </header>
        <main className="max-w-3xl mx-auto p-6 flex flex-col gap-4">
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-2" data-testid="rfi-public-header">
                <h1 className="font-serif text-2xl text-charcoal">{row.title}</h1>
                {row.intro && <div className="text-[13px] whitespace-pre-wrap">{row.intro}</div>}
                <div className="text-[12px] text-[#666]">Status: <span data-testid="rfi-public-status">{row.status}</span> · Due {row.due_at || "—"}</div>
            </section>
            <section className="flex flex-col gap-3" data-testid="rfi-public-items">
                {row.items.map((it, i) => {
                    const supportsFile = FILE_TYPES.has(it.response_type);
                    const supportsText = ["text", "text_and_files"].includes(it.response_type);
                    const supportsRef = ["reference", "reference_or_file"].includes(it.response_type);
                    const currentFiles = it.current_files || [];
                    const singleFileFilled = SINGLE_ONLY.has(it.response_type) && currentFiles.length >= 1;
                    const accept = IMAGE_ONLY.has(it.response_type) ? "image/*" : undefined;
                    const multiple = it.response_type === "multiple_files" || it.response_type === "images"
                                   || it.response_type === "text_and_files";
                    return (
                    <div key={it.id} className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-2" data-testid={`rfi-public-item-${i}`}>
                        <div className="flex items-center justify-between">
                            <div className="font-manrope font-semibold text-[13px]">{i + 1}. {it.title}</div>
                            <div className="text-[11px] uppercase text-[#666]">{labelFor(it.response_type)}</div>
                        </div>
                        {it.guidance && <div className="text-[12px] text-[#666] italic">{it.guidance}</div>}
                        {supportsText && (
                            <textarea rows={3} defaultValue={it.response_text || ""}
                                onChange={onText(it.id)}
                                data-testid={`rfi-public-text-${i}`}
                                placeholder="Your response…"
                                className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        )}
                        {supportsRef && (
                            <input defaultValue={it.response_reference || ""}
                                onChange={onRef(it.id)}
                                data-testid={`rfi-public-ref-${i}`}
                                placeholder="Reference / identifier"
                                className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        )}
                        {supportsFile && (
                            <div className="flex flex-col gap-2 border border-dashed border-[#E5E2DC] rounded-md p-2" data-testid={`rfi-public-file-panel-${i}`}>
                                <div className="flex items-center justify-between">
                                    <div className="text-[12px] text-[#666]">
                                        {SINGLE_ONLY.has(it.response_type)
                                          ? "One file only — uploading a new file replaces the current one."
                                          : "You may upload multiple files. Uploading another file will not remove earlier ones."}
                                    </div>
                                    <input
                                        ref={el => (fileInputs.current[it.id] = el)}
                                        type="file"
                                        accept={accept}
                                        multiple={multiple}
                                        className="hidden"
                                        data-testid={`rfi-public-file-input-${i}`}
                                        onChange={async (e) => {
                                            const files = Array.from(e.target.files || []);
                                            e.target.value = ""; // allow re-select same file
                                            for (const f of files) {
                                                // sequential uploads so backend state stays coherent
                                                // eslint-disable-next-line no-await-in-loop
                                                await uploadFile(it.id, f);
                                            }
                                        }} />
                                    <button
                                        onClick={() => fileInputs.current[it.id]?.click()}
                                        disabled={busyFile[it.id] || (singleFileFilled && SINGLE_ONLY.has(it.response_type) && it.response_type === "reference_or_file" && (it.response_reference || "").trim().length > 0)}
                                        data-testid={`rfi-public-file-pick-${i}`}
                                        className="text-[12px] px-2.5 py-1 border border-[#E5E2DC] rounded-md disabled:opacity-40">
                                        {singleFileFilled ? "Replace file" : (busyFile[it.id] ? "Uploading…" : "Upload")}
                                    </button>
                                </div>
                                {currentFiles.length > 0 && (
                                    <ul className="flex flex-col gap-1" data-testid={`rfi-public-file-list-${i}`}>
                                        {currentFiles.map((f, k) => (
                                            <li key={f.file_id} className="flex items-center justify-between text-[12px] bg-[#FBFAF6] border border-[#E5E2DC] rounded-md px-2 py-1"
                                                data-testid={`rfi-public-file-${i}-${k}`}>
                                                <div className="truncate">
                                                    <span className="font-medium">{f.filename || f.file_id}</span>
                                                    {f.size != null && <span className="ml-2 text-[#666]">{bytes(f.size)}</span>}
                                                </div>
                                                <button onClick={() => removeFile(it.id, f.file_id)}
                                                    disabled={busyFile[it.id]}
                                                    data-testid={`rfi-public-file-remove-${i}-${k}`}
                                                    className="text-[11px] px-2 py-0.5 border border-[#E5E2DC] rounded-md text-terracotta disabled:opacity-40">
                                                    Remove
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                        <div className="text-[11px] text-[#666]">
                            State: <span data-testid={`rfi-public-item-state-${i}`}>{it.client_state}</span>
                        </div>
                        <div className="flex gap-2">
                            {(drafts[it.id]?.text || drafts[it.id]?.reference) && (
                                <button onClick={commit(it.id)} data-testid={`rfi-public-save-${i}`}
                                    className="text-[12px] px-2.5 py-1 bg-charcoal text-white rounded-md">Save</button>
                            )}
                            {it.allow_not_available && it.client_state !== "not_available" && (
                                <button onClick={notAvailable(it.id)} data-testid={`rfi-public-na-${i}`}
                                    className="text-[12px] px-2.5 py-1 border border-[#E5E2DC] rounded-md">Not Available</button>
                            )}
                        </div>
                    </div>);
                })}
            </section>
            {submittable && (
                <section className="flex justify-end">
                    <button onClick={onSubmit} disabled={submitting}
                        data-testid="rfi-public-submit"
                        className="text-[14px] px-4 py-2 bg-charcoal text-white rounded-md disabled:opacity-40">
                        {submitting ? "Submitting…" : "Submit Requested Information"}
                    </button>
                </section>
            )}
            {msg && <div className="text-[13px] text-[#333]" data-testid="rfi-public-message">{msg}</div>}
            {error && <div className="text-[13px] text-terracotta" data-testid="rfi-public-error-inline">{error}</div>}
        </main>
    </div>);
}
